export type AuditDomain = 'bill' | 'floor' | 'menu' | 'settings' | 'order' | 'payment' | 'print'

export type AuditMeta = Record<string, string | number | boolean>

export type AuditChangeValue = string | number | null

/** Explicit before -> after for one field, so investigations do not rely on prose. */
export interface AuditChange {
  field: string
  from: AuditChangeValue
  to: AuditChangeValue
}

export interface AuditLog {
  at: string
  domain: AuditDomain
  action: string
  message: string
  actorUid: string
  actorName: string
  deviceId: string
  entityType?: string
  entityId?: string
  relatedIds?: string[]
  meta?: AuditMeta
  billId?: string
  orderId?: string
  tableId?: string
  /** Signed money impact of this action; lets the log be summed as a ledger. */
  amountDelta?: number
  reason?: string
  changes?: AuditChange[]
  /** Guest name copied from the bill at write time. */
  customerName?: string
}

export interface AuditLogDoc extends AuditLog {
  id: string
  /** Server-assigned time, resolved to ISO on read. Absent while pending. */
  serverAt?: string
}

const DOMAINS = new Set<AuditDomain>([
  'bill',
  'floor',
  'menu',
  'settings',
  'order',
  'payment',
  'print',
])

function coerceMeta(raw: unknown): AuditMeta | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: AuditMeta = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v
    }
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function coerceRelatedIds(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const ids = raw.filter((x): x is string => typeof x === 'string' && x.length > 0)
  return ids.length > 0 ? ids : undefined
}

function coerceChangeValue(raw: unknown): AuditChangeValue {
  if (typeof raw === 'string' || typeof raw === 'number') return raw
  return null
}

function coerceChanges(raw: unknown): AuditChange[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: AuditChange[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const row = entry as Record<string, unknown>
    if (typeof row.field !== 'string' || row.field.length === 0) continue
    out.push({
      field: row.field,
      from: coerceChangeValue(row.from),
      to: coerceChangeValue(row.to),
    })
  }
  return out.length > 0 ? out : undefined
}

function coerceOptionalString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined
}

function coerceOptionalNumber(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined
}

/**
 * Firestore server timestamp -> ISO. Duck-typed so this module stays
 * Firebase-free (POS, functions and future VPS API all share it).
 * Null while the local write is still pending server resolution.
 */
function coerceServerAt(raw: unknown): string | undefined {
  if (typeof raw === 'string' && raw.length > 0) return raw
  if (!raw || typeof raw !== 'object') return undefined
  const ts = raw as { toDate?: unknown; seconds?: unknown; _seconds?: unknown }
  if (typeof ts.toDate === 'function') {
    try {
      const date = (ts.toDate as () => Date)()
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString()
    } catch {
      return undefined
    }
  }
  const seconds = typeof ts.seconds === 'number' ? ts.seconds : ts._seconds
  if (typeof seconds === 'number' && Number.isFinite(seconds)) {
    return new Date(seconds * 1000).toISOString()
  }
  return undefined
}

export function coerceAuditDomain(raw: unknown): AuditDomain {
  return typeof raw === 'string' && DOMAINS.has(raw as AuditDomain)
    ? (raw as AuditDomain)
    : 'settings'
}

export function coerceAuditLog(id: string, raw: unknown): AuditLogDoc {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const entityType = typeof data.entityType === 'string' ? data.entityType : undefined
  const entityId = typeof data.entityId === 'string' ? data.entityId : undefined
  return {
    id,
    at: typeof data.at === 'string' ? data.at : new Date(0).toISOString(),
    domain: coerceAuditDomain(data.domain),
    action: typeof data.action === 'string' ? data.action : 'unknown',
    message: typeof data.message === 'string' ? data.message : '',
    actorUid: typeof data.actorUid === 'string' ? data.actorUid : '',
    actorName: typeof data.actorName === 'string' ? data.actorName : '',
    deviceId: typeof data.deviceId === 'string' ? data.deviceId : '',
    entityType: entityType || undefined,
    entityId: entityId || undefined,
    relatedIds: coerceRelatedIds(data.relatedIds),
    meta: coerceMeta(data.meta),
    serverAt: coerceServerAt(data.serverAt),
    billId: coerceOptionalString(data.billId),
    orderId: coerceOptionalString(data.orderId),
    tableId: coerceOptionalString(data.tableId),
    amountDelta: coerceOptionalNumber(data.amountDelta),
    reason: coerceOptionalString(data.reason),
    changes: coerceChanges(data.changes),
    customerName: coerceOptionalString(data.customerName),
  }
}

export function auditLogClockIso(log: Pick<AuditLogDoc, 'at' | 'serverAt'>): string {
  return log.serverAt ?? log.at
}

export function auditLogSortMs(log: Pick<AuditLogDoc, 'at' | 'serverAt'>): number {
  const ms = Date.parse(auditLogClockIso(log))
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * Union of the server-clock query and the device-clock fallback, newest first.
 * Firestore skips docs missing the ordered field, so a `serverAt` query alone
 * hides rows written before that field existed or still pending resolution.
 * Pass a positive `max` to keep only the newest N rows; omit it for the full merge.
 */
export function mergeAuditLogSnapshots(
  serverAtLogs: AuditLogDoc[],
  deviceAtLogs: AuditLogDoc[],
  max?: number,
): AuditLogDoc[] {
  const byId = new Map<string, AuditLogDoc>()
  for (const log of serverAtLogs) {
    if (log.id) byId.set(log.id, log)
  }
  for (const log of deviceAtLogs) {
    if (!log.id) continue
    const existing = byId.get(log.id)
    if (!existing) {
      byId.set(log.id, log)
      continue
    }
    if (!existing.serverAt && log.serverAt) byId.set(log.id, log)
  }
  const merged = [...byId.values()].sort((a, b) => {
    const diff = auditLogSortMs(b) - auditLogSortMs(a)
    if (diff !== 0) return diff
    return a.id.localeCompare(b.id)
  })
  if (typeof max === 'number' && max > 0) return merged.slice(0, max)
  return merged
}

export function auditDomainLabel(domain: AuditDomain): string {
  switch (domain) {
    case 'bill':
      return 'Bill'
    case 'floor':
      return 'Floor'
    case 'menu':
      return 'Menu'
    case 'settings':
      return 'Settings'
    case 'order':
      return 'Order'
    case 'payment':
      return 'Payment'
    case 'print':
      return 'Print'
  }
}
